import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { SortableTable, type SortableColumn } from '@/components/ui/SortableTable';
import { fetchSeoProjectOptions } from '@/data/sources';
import { fetchSeoCompetitorAnalysis } from '@/data/sources-seob';
import type { CompetitorAnalysisResult, CompetitorRow } from '@/types/seob';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { resolveActiveProjectId } from '@/pages/seo/domain-scope';
import { DomainChoiceGate } from '@/pages/seo/DomainChoiceGate';

/* /seo/competitor-analysis — Competitor Analysis (W2a). Read-only, source-
 * attributed list of domains competing for the project's organic footprint,
 * from DataForSEO Labs competitors_domain.
 *
 * HONESTY + SAFETY INVARIANTS (W2a contract):
 *  - PAID (DataForSEO Labs): NEVER auto-fires. Project options is a free list
 *    read; the competitor list runs ONLY on an explicit click. No useQuery is
 *    wired to the paid read, so mounting this page charges nothing.
 *  - Source-attributed (source + provider fetched_at + cache indicator); every
 *    null metric renders as an em-dash — numbers are NEVER invented.
 *  - Results render in the shared SortableTable (client-side sort only — free).
 *  - Honest states: idle / loading / error / not-configured (503).
 */

const ROW_LIMIT = 20;

function sourceLabel(source: string): string {
  if (source.startsWith('dataforseo.labs')) return 'DataForSEO Labs';
  if (source.startsWith('dataforseo')) return 'DataForSEO';
  return source;
}

function formatInt(n: number): string {
  return n.toLocaleString('en-GB');
}

/** em-dash for any null provider metric — never a fabricated value. */
function orInt(v: number | null): string {
  return v === null ? '—' : formatInt(v);
}
/** overlap ratio 0–1 → percentage; null → em-dash. */
function orPct(v: number | null): string {
  return v === null ? '—' : `${(v * 100).toFixed(1)}%`;
}

interface Panel {
  title: string;
  body: string;
}

type SectionState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; panel: Panel }
  | { status: 'done'; data: T };

function describeError(err: unknown): Panel {
  if (err instanceof ApiError) {
    if (err.status === 503 || err.code === 'provider_unavailable') {
      return {
        title: 'Competitor analysis isn’t connected yet',
        body: 'Live competitor data needs the DataForSEO Labs connection, which isn’t configured for this environment yet. Nothing was fetched and nothing was charged — an honest “not configured” state, never placeholder data.',
      };
    }
    if (err.status === 402 || err.code === 'budget_exceeded') {
      return {
        title: 'Monthly provider budget reached',
        body: 'This lookup would exceed your workspace’s monthly provider budget, so it was refused before any provider call was made — nothing was spent. It resumes when the budget resets, or after you raise the cap on Spend & Budget.',
      };
    }
    if (err.status === 429 || err.code === 'rate_limited') {
      return {
        title: 'Provider rate limit reached',
        body: 'This workspace has hit the provider rate limit, so nothing was fetched or charged. Try again as the window rolls over.',
      };
    }
    if (err.status === 502 || err.code === 'provider_error') {
      return {
        title: 'The provider request failed',
        body: `${err.message ? `${err.message} ` : ''}Nothing was stored or charged — try again in a moment.`,
      };
    }
    if (err.status === 404) {
      return {
        title: 'Project not found',
        body: 'This SEO project isn’t available to your workspace. Pick another project and try again.',
      };
    }
    return {
      title: 'The lookup could not be completed',
      body: err.message || 'The data service returned an unexpected response. Nothing was charged — try again.',
    };
  }
  return {
    title: 'The lookup could not be completed',
    body: err instanceof Error ? err.message : 'Unexpected error — nothing was charged. Try again.',
  };
}

function SourceFooter({
  source,
  fetchedAt,
  cacheHit,
}: {
  source: string;
  fetchedAt: string | null;
  cacheHit: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-border-subtle px-3 py-2 text-[11px] text-text-tertiary">
      <Badge tone="neutral">Source: {sourceLabel(source)}</Badge>
      <Badge tone={cacheHit ? 'neutral' : 'success'}>
        {cacheHit ? 'Served from cache' : 'Live result'}
      </Badge>
      <span>{fetchedAt ? `Last updated ${fetchedAt}` : 'Update time not reported'}</span>
    </div>
  );
}

const NUM_CELL = 'font-mono text-text-secondary';

const COMPETITOR_COLUMNS: SortableColumn<CompetitorRow>[] = [
  {
    key: 'domain',
    header: 'Domain',
    sortable: true,
    sortValue: (c) => c.domain,
    render: (c) => c.domain,
    truncate: true,
    maxWidthClass: 'max-w-[260px]',
    title: (c) => c.domain,
    cellClassName: 'font-medium text-text-primary',
  },
  {
    key: 'common_keywords',
    header: 'Common keywords',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (c) => c.common_keywords,
    render: (c) => orInt(c.common_keywords),
    cellClassName: `${NUM_CELL} text-text-primary`,
  },
  {
    key: 'overlap',
    header: 'Overlap',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (c) => c.overlap,
    render: (c) => orPct(c.overlap),
    cellClassName: NUM_CELL,
  },
  {
    key: 'est_traffic',
    header: 'Est. traffic /mo',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (c) => c.est_traffic,
    render: (c) => orInt(c.est_traffic),
    cellClassName: NUM_CELL,
  },
];

export function SeoCompetitorAnalysisPage() {
  const { activeWorkspace } = useAuth();
  const canManage = activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'operator';
  const navigate = useNavigate();

  const projectsQ = useQuery({
    queryKey: ['seo', 'project-options'],
    queryFn: fetchSeoProjectOptions,
  });
  const projects = projectsQ.data ?? [];
  const { active, select } = useActiveDomain();

  const projectId = resolveActiveProjectId(active, projects);

  const [competitors, setCompetitors] = useState<SectionState<CompetitorAnalysisResult>>({
    status: 'idle',
  });

  const runCompetitors = async () => {
    if (projectId === null || competitors.status === 'loading') return;
    setCompetitors({ status: 'loading' });
    try {
      const data = await fetchSeoCompetitorAnalysis(projectId, ROW_LIMIT);
      setCompetitors({ status: 'done', data });
    } catch (err) {
      setCompetitors({ status: 'error', panel: describeError(err) });
    }
  };

  const onProjectChange = (id: string) => {
    void select(Number(id));
    setCompetitors({ status: 'idle' });
  };

  if (projectsQ.isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title="Competitor Analysis" />
        <LoadingState rows={4} />
      </div>
    );
  }
  if (projectsQ.error) {
    return (
      <div className="space-y-6">
        <PageHeader title="Competitor Analysis" />
        <ErrorState error={projectsQ.error} onRetry={() => void projectsQ.refetch()} />
      </div>
    );
  }
  if (projects.length > 0 && projectId === null) {
    return <DomainChoiceGate options={projects} onSelect={(id) => void select(Number(id))} />;
  }
  if (projects.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title="Competitor Analysis" />
        <EmptyState
          title="No project to explore yet"
          teach="Competitor Analysis runs against an SEO project. Add your first domain on the Projects page, then come back here to see who competes for its organic footprint."
          action={
            <Button variant="primary" onClick={() => navigate('/seo/projects')}>
              Go to Projects
            </Button>
          }
        />
      </div>
    );
  }

  const inputClass =
    'h-control rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30';

  const projectSelector = (
    <label className="flex items-center gap-2">
      <span className="text-xs font-medium text-text-secondary">Domain</span>
      <select
        value={projectId ?? ''}
        onChange={(e) => onProjectChange(e.target.value)}
        className={inputClass}
        aria-label="SEO project"
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Competitor Analysis"
        subtitle="The domains competing for this domain's organic footprint, from licensed DataForSEO Labs data — loaded on demand, source-attributed, and honest about every missing metric."
        actions={projectSelector}
      />

      {!canManage && (
        <div role="status" className="rounded-md border border-border-subtle bg-inset/50 px-3 py-2 text-sm text-text-secondary">
          Competitor analysis uses paid provider data, so it’s limited to workspace owners and
          operators.
        </div>
      )}

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="engine">DataForSEO Labs</Badge>
          <span className="text-sm font-medium text-text-primary">Competing domains</span>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-text-tertiary">
              Paid · budget-guarded · 7-day cache · top {ROW_LIMIT}
            </span>
            <Button
              variant="primary"
              disabled={!canManage || competitors.status === 'loading'}
              onClick={() => void runCompetitors()}
              title={canManage ? undefined : 'Owner/operator only — this uses paid provider data'}
            >
              {competitors.status === 'loading'
                ? 'Loading…'
                : competitors.status === 'done'
                  ? 'Refresh'
                  : 'Load competitors'}
            </Button>
          </div>
        </div>
        <p className="mt-1 text-sm text-text-secondary">
          Domains that rank for overlapping keywords, with shared-keyword counts, overlap and
          estimated traffic. Runs only when you press the button — nothing is fetched or charged on
          load.
        </p>

        <div className="mt-4">
          {competitors.status === 'idle' && (
            <p className="text-sm text-text-tertiary">
              No data yet — load the list to see competing domains.
            </p>
          )}
          {competitors.status === 'loading' && <LoadingState rows={4} />}
          {competitors.status === 'error' && (
            <div role="status" className="rounded-md border border-border-default bg-surface px-4 py-3">
              <div className="text-sm font-medium text-text-primary">{competitors.panel.title}</div>
              <p className="mt-1 text-sm text-text-secondary">{competitors.panel.body}</p>
            </div>
          )}
          {competitors.status === 'done' &&
            (competitors.data.competitors.length === 0 ? (
              <div className="rounded-md border border-border-subtle bg-canvas p-3">
                <p className="text-sm text-text-secondary">
                  The provider returned no competing domains for this domain — an honest empty
                  result, not an error.
                </p>
                <div className="mt-2">
                  <SourceFooter
                    source={competitors.data.source}
                    fetchedAt={competitors.data.fetched_at}
                    cacheHit={competitors.data.cache_hit}
                  />
                </div>
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
                <SortableTable
                  rows={competitors.data.competitors}
                  columns={COMPETITOR_COLUMNS}
                  getRowKey={(c, i) => `${c.domain}-${i}`}
                  minWidthClass="min-w-[620px]"
                  caption="Competing domains with common keywords, overlap and estimated traffic"
                />
                <SourceFooter
                  source={competitors.data.source}
                  fetchedAt={competitors.data.fetched_at}
                  cacheHit={competitors.data.cache_hit}
                />
              </div>
            ))}
        </div>
      </Card>
    </div>
  );
}
